import { Button, ButtonLink } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useLanguageOptions } from "modules/shared/hooks/useLanguageLabel"
import { SubmitHandler, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertWhatsappOpeningMessageSchema } from "schema"
import z from "zod"
import { WhatsappOpeningMessagePreview } from "./WhatsappOpeningMessagePreview"

type WhatsappOpeningMessageFormProps = {
  form: UseFormReturn<z.infer<typeof UpsertWhatsappOpeningMessageSchema>>
  onSubmit: SubmitHandler<z.infer<typeof UpsertWhatsappOpeningMessageSchema>>
}

export const WhatsappOpeningMessageForm = ({ form, onSubmit }: WhatsappOpeningMessageFormProps) => {
  const { t } = useTranslation()
  const languageOptions = useLanguageOptions()
  const { identifier, text } = form.watch()

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex flex-col gap-4 h-full grow">
        <div className="flex flex-col gap-1">
          <CardTitle>{t("campaigns.templates.openingMessages.form.title")}</CardTitle>
          <CardDescription>
            {t("campaigns.templates.openingMessages.form.description")}
          </CardDescription>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-1 lg:col-span-2">
            <Form
              {...form}
              errorTranslations={getErrorTranslations()}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <div className="grid lg:grid-cols-2 grid-cols-1 gap-4">
                <FormField
                  control={form.control}
                  name="identifier"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>
                        {t("campaigns.templates.openingMessages.form.fields.identifier")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="locale"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>
                        {t("campaigns.templates.openingMessages.form.fields.locale")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          name={field.name}
                          required
                          value={field.value}
                          onValueChange={(value) => {
                            form.setValue("locale", value)
                          }}
                        >
                          <SelectTrigger {...field}>
                            <SelectValue
                              placeholder={t(
                                "campaigns.templates.openingMessages.form.fields.locale",
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {languageOptions.map((language) => (
                                <SelectItem key={language.value} value={language.value}>
                                  {language.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <FormField
                  control={form.control}
                  name="text"
                  render={({ field }) => (
                    <FormItem className="col-span-6 lg:col-span-4">
                      <FormLabel>
                        {t("campaigns.templates.openingMessages.form.fields.message.label")}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          {...form.register(field.name, {
                            validate: (value) => value && value.includes("{{identifier}}"),
                          })}
                          required
                          placeholder={t(
                            "campaigns.templates.openingMessages.form.fields.message.placeholder",
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!text || text.includes("{{identifier}}") ? (
                  <Label>
                    {t("campaigns.templates.openingMessages.form.fields.message.footer.default")}
                  </Label>
                ) : (
                  <Label className="text-destructive">
                    {t("campaigns.templates.openingMessages.form.fields.message.footer.error")}
                  </Label>
                )}
              </div>
            </Form>
          </div>
          <div className="col-span-1">
            <WhatsappOpeningMessagePreview
              text={text ?? ""}
              identifier={identifier ?? ""}
              className="h-full"
            />
          </div>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <ButtonLink variant={"outline"} to="/settings/channels/whatsapp/openings">
          {t("campaigns.templates.openingMessages.form.fields.cancel")}
        </ButtonLink>
        <Button disabled={!form.formState.isValid || !text} onClick={form.handleSubmit(onSubmit)}>
          {t("campaigns.templates.openingMessages.form.fields.save")}
        </Button>
      </div>
    </div>
  )
}
